import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Navbar from "../../nav_bar/Navbar";
import Footer from "../../footer/Footer";
import { fetchGrammarSubTopicById } from "./grammarService";
import "./GrammarSubTopicDetailPage.css";

/* ── Section accent colors cycling ── */
const SECTION_COLORS = [
  { border: "#019095", badge: "gsd-badge-teal",  card: "gsd-card-teal"  },
  { border: "#6366f1", badge: "gsd-badge-indigo", card: "gsd-card-indigo" },
  { border: "#10b981", badge: "gsd-badge-emerald",card: "gsd-card-emerald"},
  { border: "#f59e0b", badge: "gsd-badge-amber",  card: "gsd-card-amber"  },
  { border: "#e879f9", badge: "gsd-badge-fuchsia",card: "gsd-card-fuchsia"},
];

/* ── Toast ── */
const Toast = ({ msg, visible }) => (
  <div className={`gsd-toast ${visible ? "gsd-toast-show" : ""}`}>
    <span className="gsd-toast-icon">💡</span>
    <span>{msg}</span>
  </div>
);

/* ── Table ── */
const SectionTable = ({ tableData }) => {
  if (!tableData) return null;
  return (
    <div className="gsd-table-wrap">
      {tableData.title && <p className="gsd-table-title">{tableData.title}</p>}
      <table className="gsd-table">
        <thead>
          <tr>{tableData.headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {tableData.rows.map((row, rIdx) => (
            <tr key={rIdx}>{row.map((cell, cIdx) => <td key={cIdx}>{cell}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ── TTS hook ── */
const useTTS = (showToast) => {
  const [speaking, setSpeaking] = useState(false);

  const speak = useCallback((text) => {
    if (!("speechSynthesis" in window)) {
      showToast("Text-to-speech is not supported in this browser.");
      return;
    }
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.95;
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(utterance);
    setSpeaking(true);
  }, [speaking, showToast]);

  useEffect(() => () => window.speechSynthesis?.cancel(), []);

  return { speaking, speak };
};

/* ── Main component ── */
const GrammarSubTopicDetailPage = () => {
  const { subTopicId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backUrl = location.state?.backUrl;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ msg: "", visible: false });
  const toastTimer = useRef(null);

  const showToast = useCallback((msg) => {
    clearTimeout(toastTimer.current);
    setToast({ msg, visible: true });
    toastTimer.current = setTimeout(() => setToast(t => ({ ...t, visible: false })), 3500);
  }, []);

  const { speaking, speak } = useTTS(showToast);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const res = await fetchGrammarSubTopicById(subTopicId);
      if (res.status === 200 && res.data?.status) setData(res.data.data);
      setLoading(false);
    };
    load();
  }, [subTopicId]);

  const handleTTS = () => {
    if (!data) return;
    const text = [
      data.title,
      ...(data.sections || []).flatMap(s => [
        s.heading, s.content,
        s.example ? `Example: ${s.example}` : "",
        ...(s.subSections || []).flatMap(ss => [ss.heading, ss.content, ss.example ? `Example: ${ss.example}` : ""])
      ])
    ].filter(Boolean).join(". ");
    speak(text);
    if (!speaking) showToast("Reading aloud… tap again to stop.");
  };

  if (loading) return (<><Navbar /><div className="gsd-page"><p className="gsd-loading">Loading...</p></div><Footer /></>);
  if (!data) return (<><Navbar /><div className="gsd-page"><p className="gsd-loading">Sub-topic not found.</p></div><Footer /></>);

  return (
    <>
      <Navbar />
      <div className="gsd-page">

        {/* ── Top bar ── */}
        <div className="gsd-topbar">
          <button className="gsd-back" onClick={() => navigate(backUrl || `/study/grammar/${data.grammarTopicId}`)}>
            ← Back to {data.grammarTopicTitle || "Topic"}
          </button>
          <button className={`gsd-tts-btn ${speaking ? "gsd-tts-active" : ""}`} onClick={handleTTS} title="Read aloud">
            {speaking ? "⏹ Stop" : "🔊 Read Aloud"}
          </button>
        </div>

        {/* ── Header ── */}
        <div className="gsd-header">
          <div className="gsd-banner">
            <div className="gsd-banner-glow" />
            <span className="gsd-breadcrumb-pill">📚 {data.grammarTopicTitle}</span>
            <h1 className="gsd-title">{data.title}</h1>
            {data.sections?.length > 0 && (
              <p className="gsd-meta-pill">
                <span className="gsd-dot" />
                {data.sections.length} section{data.sections.length !== 1 ? "s" : ""}
              </p>
            )}
          </div>
        </div>

        {/* ── Sections ── */}
        {data.sections?.map((sec, idx) => {
          const color = SECTION_COLORS[idx % SECTION_COLORS.length];
          return (
            <div key={idx} className={`gsd-section ${color.card}`}>
              <div className="gsd-section-header">
                <div className={`gsd-section-num ${color.badge}`}>{idx + 1}</div>
                <h3 className="gsd-section-heading">{sec.heading}</h3>
              </div>
              <p className="gsd-section-content">{sec.content}</p>

              {sec.example && (
                <div className="gsd-example">
                  <span className="gsd-example-label">✦ Example</span>
                  <p className="gsd-example-text">{sec.example}</p>
                </div>
              )}

              <SectionTable tableData={sec.tableData} />

              {sec.subSections?.length > 0 && (
                <div className="gsd-subsections">
                  {sec.subSections.map((ss, sIdx) => (
                    <div key={sIdx} className="gsd-subsection">
                      <h4 className="gsd-subsection-heading">↳ {ss.heading}</h4>
                      <p className="gsd-section-content">{ss.content}</p>
                      {ss.example && (
                        <div className="gsd-example gsd-example-sm">
                          <span className="gsd-example-label">✦ Example</span>
                          <p className="gsd-example-text">{ss.example}</p>
                        </div>
                      )}
                      <SectionTable tableData={ss.tableData} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* ── Video ── */}
        {data.youtubeLink && (
          <div className="gsd-video-card">
            <h3 className="gsd-video-heading">🎬 Video Lesson</h3>
            <div className="gsd-video-wrapper">
              <iframe
                src={data.youtubeLink.replace("watch?v=", "embed/").replace("youtu.be/", "youtube.com/embed/")}
                title={data.title}
                allowFullScreen
                frameBorder="0"
              />
            </div>
          </div>
        )}

        {data.createdAt && (
          <p className="gsd-footer-meta">
            Added on {new Date(data.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
          </p>
        )}
      </div>

      <Toast msg={toast.msg} visible={toast.visible} />
      <Footer />
    </>
  );
};

export default GrammarSubTopicDetailPage;
